import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { UPDATE_LOAD_PROGRESS, UPDATE_LOAD_STATUS_BACKWARDS, VALIDATE_SBX_BOX } from '../../../graphql/client/mutations';
import { GET_LOAD_PROGRESS, GET_LOAD_DETAIL, GET_LOAD_LOGGERS } from '../../../graphql/client/queries';
import {
    LoadStatusEdit,
    LoadStatusEditFields,
    UpdateLoadStatusBackwards,
    ValidateSBXBox,
} from '../../../types/schemaTypes';
import { LoadStatusEnum } from '../../../types/LoadStatusEnum';
import { isValidDate } from '../../../utils';
import moment from 'moment';

interface FieldErrors {
    lofaTimeInError: string;
    lofaTimeOutError: string;
    deliveryTimeInError: string;
    deliveryTimeOutError: string;
    lofaDateInError: string;
    lofaDateOutError: string;
    deliveryDateInError: string;
    deliveryDateOutError: string;
    emptyBoxNumber1Error: string;
    emptyBoxNumber2Error: string;
}
interface Suggestion {
    id: number | string;
    name: string;
}

const ADDITIONAL_SAND_TICKET_TWENTY_FIVE_CHARS_ERROR =
    'Additional Sand Ticket should be 25 or less alphanumeric characters.';
const BOL_SIXTEEN_CHARS_ERROR = 'Sandbox BOL should be 16 or less numeric characters.';
const DELIVERY_IN_GREATER_THAN_DELIVERY_OUT_ERROR = 'Delivery date in should not be greater than delivery date out.';
const DELIVERY_IN_LESS_THAN_LOFA_OUT_ERROR = 'Delivery date in should not be less than lofa date out';
const DELIVERY_OUT_LESS_THAN_DELIVERY_IN_ERROR = 'Delivery date out should not be less than delivery date in.';
const EMPTY_BOX_ELEVEN_CHARS_ERROR = 'Empty Box must be 11 or less alphanumeric characters.';
const ENTER_VALID_DATE_ERROR = 'Please enter a valid date.';
const FUTURE_TIME_ERROR = 'Date should not be greater than current time.';
const LOFA_IN_GREATER_THAN_LOFA_OUT_ERROR = 'Lofa date in should not be greater than lofa date out.';
const LOFA_OUT_GREATER_THAN_DELIVERY_IN_ERROR = 'Lofa date out should not be greater than delivery date in.';
const LOFA_OUT_LESS_THAN_LOFA_IN_ERROR = 'Lofa date out should not be less than lofa date in.';
const MILES_NUMERIC_RANGES_ERROR = 'Miles must be numeric and less than 1000';
const REQUIRED_FOR_DELIVERED_STATUS_ERROR = 'Required to change status to Delivered.';
const REQUIRED_FOR_LOADED_STATUS_ERROR = 'Required to change status to Loaded.';
const REQUIRED_FOR_LOADING_STATUS_ERROR = 'Required to change status to Loading.';
const REQUIRED_FOR_ONSITE_STATUS_ERROR = 'Required to change status to OnSite';
const SAND_TICKET_TWENTY_FIVE_CHARS_ERROR = 'Sand Ticket should be 25 or less alphanumeric characters.';
const WEIGHT_NUMERIC_RANGES_ERROR = 'Weight must be numeric and between 1000 and 65000.';
const WEIGHT_NUMERIC_ERROR = 'Weight must be numeric';

const initialErrorsState: FieldErrors = {
    lofaTimeInError: '',
    lofaTimeOutError: '',
    deliveryTimeInError: '',
    deliveryTimeOutError: '',
    lofaDateInError: '',
    lofaDateOutError: '',
    deliveryDateInError: '',
    deliveryDateOutError: '',
    emptyBoxNumber1Error: '',
    emptyBoxNumber2Error: '',
};

const useEditLoadProgress = (
    loadProgressData: any,
    loadId: number,
    onClickHide: () => void,
    onUpdateError: (message?: string) => void,
    status: any,
): any => {
    const [inputs, setInputs] = useState({
        loadId: loadId,
        loadingDateTime: loadProgressData.loadingDateTime ? loadProgressData.loadingDateTime : '',
        loadedDateTime: loadProgressData.loadedDateTime ? loadProgressData.loadedDateTime : '',
        milesToWell: loadProgressData.milesToWell ? loadProgressData.milesToWell : null,
        onSiteDateTime: loadProgressData.onSiteDateTime ? loadProgressData.onSiteDateTime : '',
        deliveredDateTime: loadProgressData.deliveredDateTime ? loadProgressData.deliveredDateTime : '',
        sandTicketNumber: loadProgressData.sandTicketNumber ? loadProgressData.sandTicketNumber : null,
        bolNumber: loadProgressData.bolNumber ? loadProgressData.bolNumber : null,
        additionalTicketNumber: loadProgressData.additionalTicketNumber
            ? loadProgressData.additionalTicketNumber
            : null,
        weight: loadProgressData.weight ? loadProgressData.weight : null,
        grossWeight: loadProgressData.grossWeight ? loadProgressData.grossWeight : null,
        tareWeight: loadProgressData.tareWeight ? loadProgressData.tareWeight : null,
        emptyBox1: loadProgressData.emptyBox1 ? loadProgressData.emptyBox1 : null,
        emptyBox2: loadProgressData.emptyBox2 ? loadProgressData.emptyBox2 : null,
        loadStatus: status,
        sandTicketImage: loadProgressData.sandTicketImage ? loadProgressData.sandTicketImage : null,
        bolTicketImage: loadProgressData.bolTicketImage ? loadProgressData.bolTicketImage : null,
        additionalTicketImage: loadProgressData.additionalTicketImage ? loadProgressData.additionalTicketImage : null,
        emptyBoxNumber1Id: 0,
        emptyBoxNumber2Id: 0,
    });

    const [loadingErrorMessage, setLoadingErrorMessage] = useState('');
    const [loadedErrorMessage, setLoadedErrorMessage] = useState('');
    const [onSiteErrorMessage, setOnSiteErrorMessage] = useState('');
    const [deliveredErrorMessage, setDeliveredErrorMessage] = useState('');
    const [sandTicketNumberErrorMessage, setSandTicketNumberErrorMessage] = useState('');
    const [bolNumberErrorMessage, setBolNumberErrorMessage] = useState('');
    const [otherTicketNumberErrorMessage, setOtherTicketNumberErrorMessage] = useState('');
    const [weightErrorMessage, setWeightErrorMessage] = useState('');
    const [grossWeightErrorMessage, setGrossWeightErrorMessage] = useState('');
    const [tareWeightErrorMessage, setTareWeightErrorMessage] = useState('');
    const [milesToWellErrorMessage, setMilesToWellErrorMessage] = useState('');
    const [emptyBoxOneErrorMessage, setEmptyBoxOneErrorMessage] = useState('');
    const [emptyBoxTwoErrorMessage, setEmptyBoxTwoErrorMessage] = useState('');

    const [fieldErrors, setFieldErrors] = useState(initialErrorsState);
    const [permissionError, setPermissionError] = useState('');

    const handleAutoCompleteChange = (inputName: string, value: Suggestion): void => {
        switch (inputName) {
            case 'emptyBoxNumber1': {
                setInputs({
                    ...inputs,
                    emptyBoxNumber1Id: +value.id,
                    emptyBox1: value.name,
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber1Error: 'No Empty Box found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber1Error: '',
                    });
                }
                if (+value.id != 0 && inputs.emptyBoxNumber2Id == value.id) {
                    setEmptyBoxOneErrorMessage('Cannot Select Same as Empty Box2');
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber1Error: 'Cannot Select Same as Empty Box2',
                    });
                } else {
                    setEmptyBoxOneErrorMessage('');
                }
                break;
            }
            case 'emptyBoxNumber2': {
                setInputs({
                    ...inputs,
                    emptyBoxNumber2Id: +value.id,
                    emptyBox2: value.name,
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber2Error: 'No Empty Box found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber2Error: '',
                    });
                }
                if (+value.id != 0 && inputs.emptyBoxNumber1Id != 0 && inputs.emptyBoxNumber1Id == value.id) {
                    setEmptyBoxTwoErrorMessage('Cannot Select Same as Empty Box2');
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber2Error: 'Cannot Select Same as Empty Box1',
                    });
                } else {
                    setEmptyBoxTwoErrorMessage('');
                }
            }
        }
    };
    const setInputsToPreviousState = (): void => {
        setInputs({
            ...inputs,
            loadId: loadId,
            loadingDateTime: loadProgressData.loadingDateTime ? loadProgressData.loadingDateTime : '',
            loadedDateTime: loadProgressData.loadedDateTime ? loadProgressData.loadedDateTime : '',
            milesToWell: loadProgressData.milesToWell ? loadProgressData.milesToWell : null,
            onSiteDateTime: loadProgressData.onSiteDateTime ? loadProgressData.onSiteDateTime : '',
            deliveredDateTime: loadProgressData.deliveredDateTime ? loadProgressData.deliveredDateTime : '',
            sandTicketNumber: loadProgressData.sandTicketNumber ? loadProgressData.sandTicketNumber : null,
            bolNumber: loadProgressData.bolNumber ? loadProgressData.bolNumber : null,
            additionalTicketNumber: loadProgressData.additionalTicketNumber
                ? loadProgressData.additionalTicketNumber
                : null,
            weight: loadProgressData.weight ? loadProgressData.weight : null,
            grossWeight: loadProgressData.grossWeight ? loadProgressData.grossWeight : null,
            tareWeight: loadProgressData.tareWeight ? loadProgressData.tareWeight : null,
            emptyBox1: loadProgressData.emptyBox1 ? loadProgressData.emptyBox1 : null,
            emptyBox2: loadProgressData.emptyBox2 ? loadProgressData.emptyBox2 : null,
            loadStatus: status,
        });
    };

    const handleErrorForUpdateStatus = (): void => {
        setInputsToPreviousState();
        setPermissionError('You do not have permission to roll status back, please adjust your edits and try again!');
    };

    const callHideAndRemoveErrors = (): void => {
        setBolNumberErrorMessage('');
        setSandTicketNumberErrorMessage('');
        setOtherTicketNumberErrorMessage('');
        setWeightErrorMessage('');
        setGrossWeightErrorMessage('');
        setTareWeightErrorMessage('');
        setMilesToWellErrorMessage('');
        setEmptyBoxOneErrorMessage('');
        setEmptyBoxTwoErrorMessage('');
        setLoadingErrorMessage('');
        setLoadedErrorMessage('');
        setOnSiteErrorMessage('');
        setDeliveredErrorMessage('');
        setPermissionError('');
        onClickHide();
        // The interaction between loadProgress data and inputs is conflicting on
        // EditLoadProgress. Instead of relying on the refetch we refresh the page here
        // to prevent stale data after the modal is closed. - (12/10/19) BS
        window.location.reload();
    };


    const onErrorUpdateLoadProgress = (err: any): void => {
        const msg =
            err?.graphQLErrors?.[0]?.message ||
            err?.networkError?.message ||
            err?.message ||
            'Failed to update load progress.';
        onUpdateError(msg);
    };

    const [updateLoadProgress] = useMutation(UPDATE_LOAD_PROGRESS, {
        onCompleted: callHideAndRemoveErrors,
        onError: onErrorUpdateLoadProgress,
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_DETAIL,
                    variables: { loadId: loadId },
                },
                {
                    query: GET_LOAD_PROGRESS,
                    variables: { loadId: loadId },
                },
                {
                    query: GET_LOAD_LOGGERS,
                    variables: {
                        input: {
                            loadId: loadId,
                            search: '',
                        },
                    },
                },
            ];
        },
    });

    const updateLoadProgressMutation = (): any => {
        // Dynamically set the newValues, setting undefined for any properties that are not changed.
        // This is to determine which fields to edit in the backend - (12/9/19) BS
        const updateLoadProgressEditFields: any = {
            atLofaIn:
                loadProgressData.loadingDateTime !== inputs.loadingDateTime
                    ? moment(inputs.loadingDateTime).utc()
                    : undefined,
            atLofaOut:
                loadProgressData.loadedDateTime !== inputs.loadedDateTime
                    ? moment(inputs.loadedDateTime).utc()
                    : undefined,
            atDestinationIn:
                loadProgressData.onSiteDateTime !== inputs.onSiteDateTime
                    ? moment(inputs.onSiteDateTime).utc()
                    : undefined,
            atDestinationOut:
                loadProgressData.deliveredDateTime !== inputs.deliveredDateTime
                    ? moment(inputs.deliveredDateTime).utc()
                    : undefined,
            boLreceived: loadProgressData.bolNumber !== inputs.bolNumber ? inputs.bolNumber : undefined,
            sandTicket:
                loadProgressData.sandTicketNumber !== inputs.sandTicketNumber ? inputs.sandTicketNumber : undefined,
            bol2Received:
                loadProgressData.additionalTicketNumber !== inputs.additionalTicketNumber
                    ? inputs.additionalTicketNumber
                    : undefined,
            weight: loadProgressData.weight !== inputs.weight ? parseInt(inputs.weight) : undefined,
            grossWeight: loadProgressData.grossWeight !== inputs.grossWeight ? parseInt(inputs.grossWeight) : undefined,
            tareWeight: loadProgressData.tareWeight !== inputs.tareWeight ? parseInt(inputs.tareWeight) : undefined,
            miles: loadProgressData.milesToWell !== inputs.milesToWell ? parseInt(inputs.milesToWell) : undefined,
            returnBoxNumber1: inputs.emptyBoxNumber1Id ? inputs.emptyBoxNumber1Id.toString() : undefined,
            returnBoxNumber2: inputs.emptyBoxNumber2Id ? inputs.emptyBoxNumber2Id.toString() : undefined,
        };

        updateLoadProgress({
            variables: {
                input: {
                    loadId: inputs.loadId,
                    newValues: updateLoadProgressEditFields,
                    currentValues: {
                        atLofaIn: moment(loadProgressData.loadingDateTime).utc() || null,
                        atLofaOut: moment(loadProgressData.loadedDateTime).utc() || null,
                        atDestinationIn: moment(loadProgressData.onSiteDateTime).utc() || null,
                        atDestinationOut: moment(loadProgressData.deliveredDateTime).utc() || null,
                        boLreceived: loadProgressData.bolNumber || null,
                        bol2Received: loadProgressData.additionalTicketNumber || null,
                        returnBoxNumber1: loadProgressData.emptyBox1 || null,
                        returnBoxNumber2: loadProgressData.emptyBox2 || null,
                        miles: loadProgressData.milesToWell || null,
                        sandTicket: loadProgressData.sandTicketNumber || null,
                        weight: loadProgressData.weight || null,
                        grossWeight: loadProgressData.grossWeight || null,
                        tareWeight: loadProgressData.tareWeight || null,
                    },
                },
            },
        });
    };

    const onCompletedLoadStatusBackwards = (data: UpdateLoadStatusBackwards): void => {
        if (data.updateLoadStatusBackwards === null) return;
        const updateLoadStatusBackwardsErrors = data.updateLoadStatusBackwards.errors;
        if (updateLoadStatusBackwardsErrors && updateLoadStatusBackwardsErrors.length > 0) {
            if (updateLoadStatusBackwardsErrors[0] === '403 Forbidden') {
                handleErrorForUpdateStatus();
            } else {
                onUpdateError(updateLoadStatusBackwardsErrors[0] ?? 'Failed to update load status.');
            }
        } else {
            updateLoadProgressMutation();
        }
    };

    const [updateLoadStatusBackwards] = useMutation<UpdateLoadStatusBackwards>(UPDATE_LOAD_STATUS_BACKWARDS, {
        onError: handleErrorForUpdateStatus,
        onCompleted: onCompletedLoadStatusBackwards,
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_LOGGERS,
                    variables: {
                        input: {
                            loadId: loadId,
                            search: '',
                        },
                    },
                },
            ];
        },
    });

    const [validateSBXBox] = useMutation<ValidateSBXBox>(VALIDATE_SBX_BOX);

    const checkForExactStatus = (loadStatus: any): void => {
        if (loadStatus === LoadStatusEnum.CREATED) {
            setInputs({
                ...inputs,
                loadingDateTime: '',
                loadedDateTime: '',
                onSiteDateTime: '',
                deliveredDateTime: '',
                weight: '',
                grossWeight: '',
                tareWeight: '',
                bolNumber: '',
                sandTicketNumber: '',
                additionalTicketNumber: '',
                emptyBox1: '',
                emptyBox2: '',
                loadStatus: loadStatus,
            });
        }
        if (loadStatus === LoadStatusEnum.ASSIGNED) {
            setInputs({
                ...inputs,
                loadingDateTime: '',
                loadedDateTime: '',
                onSiteDateTime: '',
                deliveredDateTime: '',
                loadStatus: loadStatus,
            });
        }
        if (loadStatus === LoadStatusEnum.LOADING) {
            setInputs({
                ...inputs,
                loadedDateTime: '',
                onSiteDateTime: '',
                deliveredDateTime: '',
                loadStatus: loadStatus,
            });
        }
        if (loadStatus === LoadStatusEnum.LOADED) {
            setInputs({
                ...inputs,
                onSiteDateTime: '',
                deliveredDateTime: '',
                loadStatus: loadStatus,
            });
        }
        if (loadStatus === LoadStatusEnum.ON_SITE) {
            setInputs({
                ...inputs,
                deliveredDateTime: '',
                loadStatus: loadStatus,
            });
        }
    };

    const checkForStatuses = (loadStatus: any): void => {
        if (status === LoadStatusEnum.ASSIGNED) {
            if (loadStatus === LoadStatusEnum.CREATED) {
                checkForExactStatus(loadStatus);
            }
        }
        if (status === LoadStatusEnum.LOADING) {
            if (loadStatus === LoadStatusEnum.CREATED || loadStatus === LoadStatusEnum.ASSIGNED) {
                checkForExactStatus(loadStatus);
            }
        }
        if (status === LoadStatusEnum.LOADED) {
            if (
                loadStatus === LoadStatusEnum.CREATED ||
                loadStatus === LoadStatusEnum.ASSIGNED ||
                loadStatus === LoadStatusEnum.LOADING
            ) {
                checkForExactStatus(loadStatus);
            }
        }
        if (status === LoadStatusEnum.ON_SITE) {
            if (
                loadStatus === LoadStatusEnum.CREATED ||
                loadStatus === LoadStatusEnum.ASSIGNED ||
                loadStatus === LoadStatusEnum.LOADING ||
                loadStatus === LoadStatusEnum.LOADED
            ) {
                checkForExactStatus(loadStatus);
            }
        }
        if (status === LoadStatusEnum.DELIVERED) {
            if (
                loadStatus === LoadStatusEnum.CREATED ||
                loadStatus === LoadStatusEnum.ASSIGNED ||
                loadStatus === LoadStatusEnum.LOADING ||
                loadStatus === LoadStatusEnum.LOADED ||
                loadStatus === LoadStatusEnum.ON_SITE
            ) {
                checkForExactStatus(loadStatus);
            }
        }
        if (status === LoadStatusEnum.COMPLETED) {
            if (
                loadStatus === LoadStatusEnum.CREATED ||
                loadStatus === LoadStatusEnum.ASSIGNED ||
                loadStatus === LoadStatusEnum.LOADING ||
                loadStatus === LoadStatusEnum.LOADED ||
                loadStatus === LoadStatusEnum.ON_SITE ||
                loadStatus === LoadStatusEnum.DELIVERED
            ) {
                checkForExactStatus(loadStatus);
            }
        }
    };

    const checkForStatus = (): boolean => {
        if (status === LoadStatusEnum.ASSIGNED) {
            if (inputs.loadStatus === LoadStatusEnum.CREATED) {
                return true;
            }
        }
        if (status === LoadStatusEnum.LOADING) {
            if (inputs.loadStatus === LoadStatusEnum.CREATED || inputs.loadStatus === LoadStatusEnum.ASSIGNED) {
                return true;
            }
        }
        if (status === LoadStatusEnum.LOADED) {
            if (
                inputs.loadStatus === LoadStatusEnum.CREATED ||
                inputs.loadStatus === LoadStatusEnum.ASSIGNED ||
                inputs.loadStatus === LoadStatusEnum.LOADING
            ) {
                return true;
            }
        }
        if (status === LoadStatusEnum.ON_SITE) {
            if (
                inputs.loadStatus === LoadStatusEnum.CREATED ||
                inputs.loadStatus === LoadStatusEnum.ASSIGNED ||
                inputs.loadStatus === LoadStatusEnum.LOADING ||
                inputs.loadStatus === LoadStatusEnum.LOADED
            ) {
                return true;
            }
        }
        if (status === LoadStatusEnum.DELIVERED) {
            if (
                inputs.loadStatus === LoadStatusEnum.CREATED ||
                inputs.loadStatus === LoadStatusEnum.ASSIGNED ||
                inputs.loadStatus === LoadStatusEnum.LOADING ||
                inputs.loadStatus === LoadStatusEnum.LOADED ||
                inputs.loadStatus === LoadStatusEnum.ON_SITE
            ) {
                return true;
            }
        }
        if (status === LoadStatusEnum.COMPLETED) {
            if (
                inputs.loadStatus === LoadStatusEnum.CREATED ||
                inputs.loadStatus === LoadStatusEnum.ASSIGNED ||
                inputs.loadStatus === LoadStatusEnum.LOADING ||
                inputs.loadStatus === LoadStatusEnum.LOADED ||
                inputs.loadStatus === LoadStatusEnum.ON_SITE ||
                inputs.loadStatus === LoadStatusEnum.DELIVERED
            ) {
                return true;
            }
        }
        return false;
    };

    const handleDropdownChange = (fieldName: string, newValue: string): void => {
        setInputs(inputs => ({ ...inputs, [fieldName]: newValue }));
        checkForStatuses(newValue);
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
        checkForStatuses(event.target.value);
    };

    const setMilesErrorEmpty = (): void => {
        setMilesToWellErrorMessage('');
    };

    const setWeightErrorEmpty = (): void => {
        setWeightErrorMessage('');
    };

    const setGrossWeightErrorEmpty = (): void => {
        setGrossWeightErrorMessage('');
    };

    const setTareWeightErrorEmpty = (): void => {
        setTareWeightErrorMessage('');
    };

    const setBolNumberErrorEmpty = (): void => {
        setBolNumberErrorMessage('');
    };

    const setSandNumberErrorEmpty = (): void => {
        setSandTicketNumberErrorMessage('');
    };

    const setOtherTicketNumberErrorEmpty = (): void => {
        setOtherTicketNumberErrorMessage('');
    };

    const handleDateInputChange = (inputName: string, value: Date): void => {
        switch (inputName) {
            case 'loadingDateTime': {
                setLoadingErrorMessage('');
                setInputs({
                    ...inputs,
                    loadingDateTime: value,
                });
                break;
            }
            case 'loadedDateTime': {
                setLoadedErrorMessage('');
                setInputs({
                    ...inputs,
                    loadedDateTime: value,
                });
                break;
            }
            case 'onSiteDateTime': {
                setOnSiteErrorMessage('');
                setInputs({
                    ...inputs,
                    onSiteDateTime: value,
                });
                break;
            }
            case 'deliveredDateTime': {
                setDeliveredErrorMessage('');
                setInputs({
                    ...inputs,
                    deliveredDateTime: value,
                });
                break;
            }
        }
    };

    const customValidation = (): boolean => {
        if (inputs.loadStatus === LoadStatusEnum.LOADING && inputs.loadingDateTime === '') {
            setLoadingErrorMessage(REQUIRED_FOR_LOADING_STATUS_ERROR);
            return false;
        }
        if (
            (inputs.loadStatus === LoadStatusEnum.LOADED &&
                (inputs.loadedDateTime === '' || inputs.loadingDateTime === '')) ||
            (inputs.loadedDateTime !== '' && inputs.loadingDateTime === '')
        ) {
            if (inputs.loadedDateTime === '') {
                setLoadedErrorMessage(REQUIRED_FOR_LOADED_STATUS_ERROR);
            }
            if (inputs.loadingDateTime === '') {
                setLoadingErrorMessage(REQUIRED_FOR_LOADED_STATUS_ERROR);
            }
            return false;
        }
        if (
            (inputs.loadStatus === LoadStatusEnum.ON_SITE &&
                (inputs.loadedDateTime === '' || inputs.loadingDateTime === '' || inputs.onSiteDateTime === '')) ||
            (inputs.loadedDateTime === '' && inputs.loadingDateTime !== '' && inputs.onSiteDateTime !== '') ||
            (inputs.loadedDateTime !== '' && inputs.loadingDateTime === '' && inputs.onSiteDateTime !== '')
        ) {
            if (inputs.loadedDateTime === '') {
                setLoadedErrorMessage(REQUIRED_FOR_ONSITE_STATUS_ERROR);
            }
            if (inputs.loadingDateTime === '') {
                setLoadingErrorMessage(REQUIRED_FOR_ONSITE_STATUS_ERROR);
            }
            if (inputs.onSiteDateTime === '') {
                setOnSiteErrorMessage(REQUIRED_FOR_ONSITE_STATUS_ERROR);
            }
            return false;
        }
        if (
            ((inputs.loadStatus === LoadStatusEnum.DELIVERED || !!inputs.deliveredDateTime) &&
                (inputs.loadedDateTime === '' ||
                    inputs.loadingDateTime === '' ||
                    inputs.onSiteDateTime === '' ||
                    inputs.deliveredDateTime === '' ||
                    inputs.bolNumber === '' ||
                    inputs.weight === '' ||
                    inputs.weight === 0 ||
                    !inputs.weight ||
                    inputs.grossWeight === '' ||
                    inputs.grossWeight === 0 ||
                    !inputs.grossWeight ||
                    inputs.tareWeight === '' ||
                    inputs.tareWeight === 0 ||
                    !inputs.tareWeight ||
                    inputs.milesToWell === '' ||
                    !inputs.milesToWell ||
                    inputs.sandTicketNumber === '' ||
                    (loadProgressData.additionalTicketImage !== '' &&
                        (inputs.additionalTicketNumber === '' || !inputs.additionalTicketNumber)))) ||
            (inputs.deliveredDateTime !== '' &&
                (inputs.loadedDateTime === '' ||
                    inputs.loadingDateTime === '' ||
                    inputs.onSiteDateTime === '' ||
                    inputs.bolNumber === '' ||
                    inputs.weight === '' ||
                    inputs.weight === 0 ||
                    inputs.grossWeight === '' ||
                    inputs.grossWeight === 0 ||
                    inputs.tareWeight === '' ||
                    inputs.tareWeight === 0 ||
                    inputs.milesToWell === '' ||
                    !inputs.milesToWell ||
                    inputs.sandTicketNumber === '' ||
                    (loadProgressData.additionalTicketImage !== '' &&
                        (inputs.additionalTicketNumber === '' || !inputs.additionalTicketNumber))))
        ) {
            if (inputs.loadedDateTime === '') {
                setLoadedErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.loadingDateTime === '') {
                setLoadingErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.onSiteDateTime === '') {
                setOnSiteErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.deliveredDateTime === '') {
                setDeliveredErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.bolNumber === '' || !inputs.bolNumber) {
                setBolNumberErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.weight === '' || inputs.weight === 0 || !inputs.weight) {
                setWeightErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.grossWeight === '' || inputs.grossWeight === 0 || !inputs.grossWeight) {
                setGrossWeightErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.tareWeight === '' || inputs.tareWeight === 0 || !inputs.tareWeight) {
                setTareWeightErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.milesToWell === '' || !inputs.milesToWell) {
                setMilesToWellErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (inputs.sandTicketNumber === '' || !inputs.sandTicketNumber) {
                setSandTicketNumberErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            if (
                loadProgressData.additionalTicketImage !== '' &&
                (inputs.additionalTicketNumber === '' || !inputs.additionalTicketNumber)
            ) {
                setOtherTicketNumberErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            }
            return false;
        }

        return true;
    };

    const alphanumeric = /^[a-z0-9-]+$/i;
    const numeric = /^[0-9]*$/;

    const validateSandTicket = (value: any): void => {
        const sandTicket = value ? value.toString() : '';

        if (sandTicket.length === 0) {
            setSandTicketNumberErrorMessage('');
        } else {
            if (sandTicket.length > 25 || !alphanumeric.test(sandTicket)) {
                setSandTicketNumberErrorMessage(SAND_TICKET_TWENTY_FIVE_CHARS_ERROR);
            } else {
                setSandTicketNumberErrorMessage('');
            }
        }
    };

    const validateAdditionalSandTicket = (value: any): void => {
        const sandTicket = value ? value.toString() : '';

        if (sandTicket.length === 0) {
            setOtherTicketNumberErrorMessage('');
        } else {
            if (sandTicket.length > 25 || !alphanumeric.test(sandTicket)) {
                setOtherTicketNumberErrorMessage(ADDITIONAL_SAND_TICKET_TWENTY_FIVE_CHARS_ERROR);
            } else {
                setOtherTicketNumberErrorMessage('');
            }
        }
    };

    const validateSandboxBOL = (value: any): void => {
        const sandboxBol = value ? value.toString() : '';

        if (sandboxBol.length === 0) {
            setBolNumberErrorMessage('');
        } else {
            if (sandboxBol.length > 16 || !numeric.test(sandboxBol)) {
                setBolNumberErrorMessage(BOL_SIXTEEN_CHARS_ERROR);
            } else {
                setBolNumberErrorMessage('');
            }
        }
    };

    const validateWeight = (value: any): void => {
        const weight = value ? value.toString() : '';

        if (weight.length === 0) {
            setWeightErrorMessage('');
            return;
        }

        if (!numeric.test(weight)) {
            setWeightErrorMessage(WEIGHT_NUMERIC_RANGES_ERROR);
            return;
        }

        const numWeight = parseInt(weight);

        if (numWeight < 1000 || numWeight > 65000) {
            setWeightErrorMessage(WEIGHT_NUMERIC_RANGES_ERROR);
        } else {
            setWeightErrorMessage('');
        }
    };

    const validateGrossWeight = (value: any): void => {
        const grossWeight = value ? value.toString() : '';
        if (!numeric.test(grossWeight)) {
            setGrossWeightErrorMessage(WEIGHT_NUMERIC_ERROR);
        } else {
            setGrossWeightErrorMessage('');
        }
    };

    const validateTareWeight = (value: any): void => {
        const tareWeight = value ? value.toString() : '';
        if (!numeric.test(tareWeight)) {
            setTareWeightErrorMessage(WEIGHT_NUMERIC_ERROR);
        } else {
            setTareWeightErrorMessage('');
        }
    };

    const validateMiles = (value: any): void => {
        const miles = value ? value.toString() : '';

        if (miles.length === 0) {
            setMilesToWellErrorMessage('');
        } else {
            if (miles.length > 3 || !numeric.test(miles)) {
                setMilesToWellErrorMessage(MILES_NUMERIC_RANGES_ERROR);
            } else {
                setMilesToWellErrorMessage('');
            }
        }
    };

    const validateEmptyBox1 = (value: any): void => {
        const box = value ? value.toString() : '';

        if (box.length === 0) {
            setEmptyBoxOneErrorMessage('');
        } else {
            if (box.length > 11 || !alphanumeric.test(box)) {
                setEmptyBoxOneErrorMessage(EMPTY_BOX_ELEVEN_CHARS_ERROR);
            } else {
                setEmptyBoxOneErrorMessage('');
            }
        }
    };

    const validateEmptyBox2 = (value: any): void => {
        const box = value ? value.toString() : '';

        if (box.length === 0) {
            setEmptyBoxTwoErrorMessage('');
        } else {
            if (box.length > 11 || !alphanumeric.test(box)) {
                setEmptyBoxTwoErrorMessage(EMPTY_BOX_ELEVEN_CHARS_ERROR);
            } else {
                setEmptyBoxTwoErrorMessage('');
            }
        }
    };

    const validateLofaDateIn = (date: string): void => {
        const lofaDateIn = !!date ? new Date(date) : '';
        let lofaDateInError = '';
        let lofaDateOutError = fieldErrors.lofaDateOutError;

        if (inputs && lofaDateIn) {
            if (inputs.loadedDateTime) {
                if (lofaDateIn > new Date(inputs.loadedDateTime)) {
                    lofaDateInError = LOFA_IN_GREATER_THAN_LOFA_OUT_ERROR;
                }
                if (new Date(inputs.loadedDateTime) > lofaDateIn) {
                    if (inputs.onSiteDateTime && new Date(inputs.onSiteDateTime) > new Date(inputs.loadedDateTime)) {
                        lofaDateOutError = '';
                    } else if (!inputs.loadedDateTime) {
                        lofaDateOutError = '';
                    }
                }
            }

            if (lofaDateIn > new Date()) {
                lofaDateInError = FUTURE_TIME_ERROR;
            }

            if (!isValidDate(lofaDateIn)) {
                lofaDateInError = ENTER_VALID_DATE_ERROR;
            }
        }
        setFieldErrors({
            ...fieldErrors,
            lofaDateInError,
            lofaDateOutError,
        });
    };

    const validateLofaDateOut = (date: string): void => {
        const lofaDateOut = !!date ? new Date(date) : '';
        let lofaDateOutError = '';
        let lofaDateInError = fieldErrors.lofaDateInError;
        let deliveryDateInError = fieldErrors.deliveryDateInError;
        if (inputs && lofaDateOut) {
            if (inputs.loadingDateTime) {
                if (lofaDateOut < new Date(inputs.loadingDateTime)) {
                    lofaDateOutError = LOFA_OUT_LESS_THAN_LOFA_IN_ERROR;
                }
                if (new Date(inputs.loadingDateTime) < lofaDateOut) {
                    lofaDateInError = lofaDateInError !== LOFA_IN_GREATER_THAN_LOFA_OUT_ERROR ? lofaDateInError : '';
                }
            }
            if (lofaDateOut > new Date()) {
                lofaDateOutError = FUTURE_TIME_ERROR;
            }
            if (inputs.onSiteDateTime) {
                if (lofaDateOut > new Date(inputs.onSiteDateTime)) {
                    lofaDateOutError = LOFA_OUT_GREATER_THAN_DELIVERY_IN_ERROR;
                }
                if (new Date(inputs.onSiteDateTime) > lofaDateOut) {
                    if (
                        inputs.deliveredDateTime &&
                        new Date(inputs.deliveredDateTime) > new Date(inputs.onSiteDateTime)
                    ) {
                        deliveryDateInError = '';
                    } else if (!inputs.onSiteDateTime) {
                        deliveryDateInError = '';
                    }
                }
            }

            if (!isValidDate(lofaDateOut)) {
                lofaDateOutError = ENTER_VALID_DATE_ERROR;
            }
        }

        setFieldErrors({
            ...fieldErrors,
            lofaDateOutError,
            lofaDateInError,
            deliveryDateInError,
        });
    };

    const validateDeliveryDateIn = (date: string): void => {
        const deliveryDateIn = !!date ? new Date(date) : '';
        let deliveryDateInError = '';
        let lofaDateOutError = fieldErrors.lofaDateOutError;
        let deliveryDateOutError = fieldErrors.deliveryDateOutError;
        if (inputs && deliveryDateIn) {
            if (inputs.deliveredDateTime) {
                if (deliveryDateIn > new Date(inputs.deliveredDateTime)) {
                    deliveryDateInError = DELIVERY_IN_GREATER_THAN_DELIVERY_OUT_ERROR;
                }
                if (
                    deliveryDateIn < new Date(inputs.deliveredDateTime) &&
                    new Date(inputs.deliveredDateTime) < new Date() &&
                    new Date(deliveryDateIn) < new Date()
                ) {
                    deliveryDateOutError = '';
                }
            }
            if (deliveryDateIn > new Date()) {
                deliveryDateInError = FUTURE_TIME_ERROR;
            }
            if (inputs.loadedDateTime) {
                if (deliveryDateIn < new Date(inputs.loadedDateTime)) {
                    deliveryDateInError = DELIVERY_IN_LESS_THAN_LOFA_OUT_ERROR;
                }
                if (
                    inputs.loadingDateTime &&
                    new Date(inputs.loadedDateTime) < deliveryDateIn &&
                    new Date(inputs.loadedDateTime) < new Date() &&
                    new Date(deliveryDateIn) < new Date()
                ) {
                    lofaDateOutError = '';
                }
            }
            if (!isValidDate(deliveryDateIn)) {
                deliveryDateInError = ENTER_VALID_DATE_ERROR;
            }
        }

        setFieldErrors({
            ...fieldErrors,
            deliveryDateInError,
            lofaDateOutError,
            deliveryDateOutError,
        });
    };

    const validateDeliveryDateOut = (date: string): void => {
        const deliveryDateOut = !!date ? new Date(date) : '';
        let deliveryDateOutError = '';
        let deliveryDateInError = fieldErrors.deliveryDateInError;

        if (inputs && deliveryDateOut) {
            if (inputs.onSiteDateTime) {
                if (deliveryDateOut < new Date(inputs.onSiteDateTime)) {
                    deliveryDateOutError = DELIVERY_OUT_LESS_THAN_DELIVERY_IN_ERROR;
                }
                if (
                    inputs.loadedDateTime &&
                    new Date(inputs.onSiteDateTime) < deliveryDateOut &&
                    new Date(inputs.onSiteDateTime) > new Date(inputs.loadedDateTime) &&
                    new Date(inputs.onSiteDateTime) < new Date() &&
                    new Date(deliveryDateOut) < new Date()
                ) {
                    deliveryDateInError = '';
                }
            }
            if (deliveryDateOut > new Date()) {
                deliveryDateOutError = FUTURE_TIME_ERROR;
            }
            if (!isValidDate(deliveryDateOut)) {
                deliveryDateOutError = ENTER_VALID_DATE_ERROR;
            }

            if (!inputs.bolNumber) setBolNumberErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            if (!inputs.sandTicketNumber) setSandTicketNumberErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            if (loadProgressData.additionalTicketImage !== '' && !inputs.additionalTicketNumber)
                setOtherTicketNumberErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            if (inputs.weight === '' || inputs.weight === 0 || !inputs.weight)
                setWeightErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            if (inputs.grossWeight === '' || inputs.grossWeight === 0 || !inputs.grossWeight)
                setGrossWeightErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            if (inputs.tareWeight === '' || inputs.tareWeight === 0 || !inputs.tareWeight)
                setTareWeightErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
            if (inputs.milesToWell === '' || !inputs.milesToWell)
                setMilesToWellErrorMessage(REQUIRED_FOR_DELIVERED_STATUS_ERROR);
        } else {
            setBolNumberErrorEmpty();
            setSandNumberErrorEmpty();
            setOtherTicketNumberErrorEmpty();
            setWeightErrorEmpty();
            setGrossWeightErrorEmpty();
            setTareWeightErrorEmpty();
            setMilesErrorEmpty();
        }

        setFieldErrors({
            ...fieldErrors,
            deliveryDateOutError,
            deliveryDateInError,
        });
    };

    const handleCancel = (): void => {
        setInputsToPreviousState();
        setFieldErrors({
            ...fieldErrors,
            lofaTimeInError: '',
            lofaTimeOutError: '',
            deliveryTimeInError: '',
            deliveryTimeOutError: '',
            lofaDateInError: '',
            lofaDateOutError: '',
            deliveryDateInError: '',
            deliveryDateOutError: '',
        });
        setBolNumberErrorMessage('');
        setSandTicketNumberErrorMessage('');
        setOtherTicketNumberErrorMessage('');
        setWeightErrorMessage('');
        setGrossWeightErrorMessage('');
        setTareWeightErrorMessage('');
        setMilesToWellErrorMessage('');
        setEmptyBoxOneErrorMessage('');
        setEmptyBoxTwoErrorMessage('');
        setLoadingErrorMessage('');
        setLoadedErrorMessage('');
        setOnSiteErrorMessage('');
        setDeliveredErrorMessage('');
        setPermissionError('');
    };

    const validateAllBoxes = async (): Promise<boolean> => {
        let hasValidationErrors = false;
        
        // Clear previous box errors
        setEmptyBoxOneErrorMessage('');
        setEmptyBoxTwoErrorMessage('');
        setFieldErrors(prev => ({
            ...prev,
            emptyBoxNumber1Error: '',
            emptyBoxNumber2Error: '',
        }));

        // Validate empty box 1 if selected
        if (inputs.emptyBoxNumber1Id && inputs.emptyBoxNumber1Id !== 0) {
            try {
                const response = await validateSBXBox({
                    variables: {
                        input: {
                            boxId: inputs.emptyBoxNumber1Id,
                            boxNumber: inputs.emptyBox1,
                            isBox: false,
                            isReturnBox: true,
                            returnBoxId1: inputs.emptyBoxNumber1Id,
                            returnBoxId2: 0,
                            returnBoxName1: inputs.emptyBox1,
                            returnBoxName2: null
                        }
                    }
                });
                
                const errMsg1 = response.data?.validateSBXBox?.errorMessage ?? '';
                if (errMsg1) {
                    setEmptyBoxOneErrorMessage(errMsg1);
                    setFieldErrors(prev => ({
                        ...prev,
                        emptyBoxNumber1Error: errMsg1,
                    }));
                    hasValidationErrors = true;
                }
            } catch (err: any) {
                if (err.graphQLErrors && err.graphQLErrors.length > 0) {
                    const { message } = err.graphQLErrors[0];
                    setEmptyBoxOneErrorMessage(message.toUpperCase());
                    setFieldErrors(prev => ({
                        ...prev,
                        emptyBoxNumber1Error: message.toUpperCase(),
                    }));
                    hasValidationErrors = true;
                } else {
                    console.log("no error");
                }
            }
        }

        // Validate empty box 2 if selected
        if (inputs.emptyBoxNumber2Id && inputs.emptyBoxNumber2Id !== 0) {
            try {
                const response = await validateSBXBox({
                    variables: {
                        input: {
                            boxId: inputs.emptyBoxNumber2Id,
                            boxNumber: inputs.emptyBox2,
                            isBox: false,
                            isReturnBox: true,
                            returnBoxId1: 0,
                            returnBoxId2: inputs.emptyBoxNumber2Id,
                            returnBoxName1: null,
                            returnBoxName2: inputs.emptyBox2
                        }
                    }
                });
                
                const errMsg2 = response.data?.validateSBXBox?.errorMessage ?? '';
                if (errMsg2) {
                    setEmptyBoxTwoErrorMessage(errMsg2);
                    setFieldErrors(prev => ({
                        ...prev,
                        emptyBoxNumber2Error: errMsg2,
                    }));
                    hasValidationErrors = true;
                }
            } catch (err: any) {
                if (err.graphQLErrors && err.graphQLErrors.length > 0) {
                    const { message } = err.graphQLErrors[0];
                    setEmptyBoxTwoErrorMessage(message.toUpperCase());
                    setFieldErrors(prev => ({
                        ...prev,
                        emptyBoxNumber2Error: message.toUpperCase(),
                    }));
                    hasValidationErrors = true;
                } else {
                    console.log("no error");
                }
            }
        }

        return !hasValidationErrors;
    };

    const handleSubmit = async (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): Promise<void> => {
        if (event) {
            event.preventDefault();
        }

        // Validate all boxes first
        const boxesValid = await validateAllBoxes();
        
        if (!boxesValid) {
            return; // Stop if box validation failed
        }

        if (checkForStatus()) {
            const updateLoadStatusEditFields: LoadStatusEditFields = {
                oldStatus: status,
                newStatus: inputs.loadStatus,
            };

            const updateLoadStatusEdit: LoadStatusEdit = {
                loadId: inputs.loadId,
                loadStatusEdit: updateLoadStatusEditFields,
            };
            updateLoadStatusBackwards({
                variables: { input: updateLoadStatusEdit },
            });
        } else if (!customValidation()) {
        } else {
            updateLoadProgressMutation();
        }
    };

    return {
        handleSubmit,
        handleDropdownChange,
        handleAutoCompleteChange,
        handleInputChange,
        handleDateInputChange,
        inputs,
        fieldErrors,
        validateLofaDateIn,
        validateLofaDateOut,
        validateDeliveryDateIn,
        validateDeliveryDateOut,
        validateSandTicket,
        validateAdditionalSandTicket,
        validateSandboxBOL,
        validateWeight,
        validateGrossWeight,
        validateTareWeight,
        validateMiles,
        validateEmptyBox1,
        validateEmptyBox2,
        handleCancel,
        loadingErrorMessage,
        loadedErrorMessage,
        onSiteErrorMessage,
        deliveredErrorMessage,
        sandTicketNumberErrorMessage,
        bolNumberErrorMessage,
        otherTicketNumberErrorMessage,
        weightErrorMessage,
        grossWeightErrorMessage,
        tareWeightErrorMessage,
        milesToWellErrorMessage,
        emptyBoxOneErrorMessage,
        emptyBoxTwoErrorMessage,
        permissionError,
    };
};

export default useEditLoadProgress;
